import { createContext, useContext, useState, type ReactNode } from 'react'
import type { BPlateSectionButton } from '../types'

// A dedicated module workspace shell (GarageDashboardLayout, OpenPlaceLayout) renders its navbar
// OUTSIDE the <Outlet/> that mounts the currently-loaded Plate (PlateViewerPage) — so the shell
// has no way to know that Plate's own headerTitle/global buttons without something bridging the
// two. This context is that bridge: the shell provides it, PlateViewerPage populates it once its
// Plate query resolves and clears it on unmount/plate change.
export interface PlateChrome {
  headerTitle?: string
  globalButtons: BPlateSectionButton[]
  runInputs: Record<string, string>
}

interface PlateChromeContextValue {
  chrome: PlateChrome | null
  setChrome: (chrome: PlateChrome | null) => void
}

const PlateChromeContext = createContext<PlateChromeContextValue | null>(null)

export function PlateChromeProvider({ children }: { children: ReactNode }) {
  const [chrome, setChrome] = useState<PlateChrome | null>(null)
  return (
    <PlateChromeContext.Provider value={{ chrome, setChrome }}>
      {children}
    </PlateChromeContext.Provider>
  )
}

// Consumed by a workspace shell's own navbar to read the current Plate's title/buttons.
export function usePlateChrome(): PlateChrome | null {
  return useContext(PlateChromeContext)?.chrome ?? null
}

// Consumed by PlateViewerPage (the only setter) to publish the currently-loaded Plate's chrome
// up to whichever shell is rendering it. A no-op outside a PlateChromeProvider (e.g. OpenPlace's
// public token page, which has no workspace navbar to publish to).
export function useSetPlateChrome(): (chrome: PlateChrome | null) => void {
  const ctx = useContext(PlateChromeContext)
  return ctx?.setChrome ?? (() => {})
}
